import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FormErrors } from '@shared/ui-react/UtilComponents';
import { FiletreeSelectReact } from '@shared/ui-react/FiletreeSelectReact';
import { IconPicker } from '@shared/ui-react/IconPicker';
import { LatLonMapPicker } from '@openlayers/LatLonMapPicker';

export function DocumentEditForm({ treeData, isBulk = false, hasUrl = false, mapWrapper, mapTools, activateEditLocation }) {
    const {
        register,
        getValues,
        setValue,
        watch,
        formState: { errors },
        control,
    } = useFormContext<
        IDocument & {
            latLon: [number, number];
            nameEnabled: boolean;
            descEnabled: boolean;
            iconEnabled: boolean;
            locationEnabled: boolean;
            folderEnabled: boolean;
        }
    >();

    return (
        <>
            <div className='form-group'>
                <label>
                    {isBulk && <input type='checkbox' {...register('nameEnabled', { deps: ['name'] })} />}
                    <span>{__('general.name') + ' *'}</span>
                </label>
                <input
                    type='text'
                    {...register('name', {
                        disabled: isBulk ? !watch('nameEnabled') : false,
                        required: true,
                        maxLength: 250,
                    })}
                />
                <FormErrors error={errors.name as any} maxLength={250} />
            </div>

            {!isBulk && hasUrl && (
                <div className='form-group'>
                    <label>
                        <span>URL *</span>
                    </label>
                    <input
                        type='text'
                        {...register('url', { required: true, maxLength: 512, pattern: /^https:\/\/[^ "]+$|^<iframe[^>]*>([^<]*(<a[^>]*>[^<]*<\/a>[^<]*)*)<\/iframe>$/ })}
                    />
                    <FormErrors error={errors.url as any} maxLength={512} />
                </div>
            )}

            <div className='form-group'>
                <label>
                    {isBulk && <input type='checkbox' {...register('descEnabled', { deps: ['description'] })} />}
                    <span>{__('general.description')}</span>
                </label>
                <textarea
                    rows={3}
                    {...register('description', {
                        disabled: isBulk ? !watch('descEnabled') : false,
                        required: false,
                        maxLength: 250,
                    })}
                />
                <FormErrors error={errors.description as any} maxLength={250} />
            </div>

            <div className='form-group'>
                <label>
                    {isBulk && <input type='checkbox' {...register('folderEnabled', { deps: ['folderUuid'] })} />}
                    <span>{__('js_files.create_folder.parent_folder')}</span>
                </label>
                <FiletreeSelectReact
                    selectable='folder'
                    includeRoot={false}
                    fetchAccess={true}
                    useUuid={true}
                    treeData={treeData}
                    selectedId={getValues('folderUuid')}
                    disabled={isBulk ? !watch('folderEnabled') : false}
                    storageKey={'DocumentsEditor_folderSelectTree'}
                    onChange={uuid => setValue('folderUuid', uuid, { shouldDirty: true, shouldValidate: true })}
                />
            </div>

            <div className='form-group'>
                <label>
                    {isBulk && <input type='checkbox' {...register('iconEnabled', { deps: ['icon'] })} />}
                    <span>{__('js_files.icon')}</span>
                </label>
                <Controller
                    name='icon'
                    control={control}
                    rules={{ required: false }}
                    render={({ field, fieldState }) => (
                        <IconPicker selectedId={field.value} onChange={iconId => field.onChange(iconId)} disabled={isBulk ? !watch('iconEnabled') : false} />
                    )}
                />
            </div>

            <div className='form-group'>
                <label>
                    {isBulk && <input type='checkbox' {...register('locationEnabled', { deps: ['latLon'] })} />}
                    <span>{__('general.location')}</span>
                </label>

                <Controller
                    name='latLon'
                    control={control}
                    rules={{ required: false }}
                    render={({ field, fieldState }) => (
                        <LatLonMapPicker
                            latLon={field.value}
                            onChange={latLon => field.onChange(latLon)}
                            disabled={isBulk ? !watch('locationEnabled') : false}
                            mapWrapper={mapWrapper}
                            mapTools={mapTools}
                            activateOnMount={activateEditLocation}
                        />
                    )}
                />
            </div>
        </>
    );
}
